import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { VendorProduct } from '../vendorproducts/vendorproducts.service';

export interface CompanyInfo {
  id: string;
  name: string;
  type?: string;
  payrollConfig: any;
  resolvedPayrollConfig?: any;
  domainName: string;
  parentId: string;
}

export interface PayrollWorkspaceTeamConfiguration {
  name?: string;
  autoAddNewMembers?: boolean;
  membershipManagedBy?: string;
  payrollProviderSlug?: string;
  payrollTeamSource?: string;
  snapshotCreatedAt?: string;
  originalFilters?: {
    fieldsContain?: Record<string, string | string[]>;
    fieldsExclude?: Record<string, string | string[]>;
  };
  criteriaSnapshot?: {
    activeOnly?: boolean;
    criteria: Array<{
      field: string;
      operator: string;
      value: string | string[];
      label?: string;
      displayValue?: string;
    }>;
    eligibleStatuses?: string[];
    match?: string;
    missingEmailPolicy?: string;
    mode?: string;
    providerSlug?: string;
  };
}

export interface PayrollWorkspaceCompany {
  id: string;
  name: string;
  type: string;
  payrollConfig: {
    activeProducts?: string[];
    plan?: string;
    plusPlanIncluded?: boolean;
    payrollAdminTeams?: {
      defaultPlan?: string;
      plusPlanIncluded?: boolean;
    };
    teamsMap?: Record<string, PayrollWorkspaceTeamConfiguration>;
  };
  resolvedPayrollConfig: {
    products: string[];
    alwaysActiveProducts: string[];
    allowanceControlDisabledProducts: string[];
    productPlanIdsMap: Record<string, string[]>;
    productPlanLabelsMap: Record<string, string[]>;
    productPayrollBenefitCategoriesMap: Record<string, string | {
      category: string;
      subcategory?: string;
      requiresApproval?: boolean;
    }>;
    payrollBenefitSubcategoryDefinitionsByCategory: Record<string, Array<{
      id: string;
      label: string;
    }>>;
  };
  snackBrand: string;
}

export interface PayrollCompanyWorkspaceContext {
  company: PayrollWorkspaceCompany;
}

export type CompanyGroupProductOnboardingStatus = 'not_requested' | 'pending' | 'ready';

export interface CompanyGroupProductOnboardingUser {
  userId: string;
  name: string;
  email: string;
  phone?: string;
}

export interface CompanyGroupProductOnboardingRow {
  companyId: string;
  vendorGroupProductId?: string;
  requestedVendorProductId: string;
  providerName: string;
  planName: string;
  status: CompanyGroupProductOnboardingStatus;
  requestedAt: string;
  requestedBy: CompanyGroupProductOnboardingUser;
  requestNote: string;
  readyAt: string;
  readyBy: CompanyGroupProductOnboardingUser;
  notificationSentAt: string;
  companyName?: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
}

export interface PayrollCompanyBrokerNotificationBroker {
  id?: string;
  companyName?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  categories?: string[];
}

export interface PayrollCompanyBrokerNotificationResult {
  diffSummary: {
    added: number;
    updated: number;
    removed: number;
  };
  emailAttempted: boolean;
  emailSent: boolean;
  skippedReason?: 'no-diff' | 'flag-disabled' | 'flag-unavailable';
  warning?: string;
}

export interface PayrollAdminBankConnection {
  stripeCustomerId?: string;
  stripePaymentMethod?: string;
  stripeLast4?: string;
  paymentTargets?: string[];
  connectedAt?: string;
  updatedAt?: string;
  bankInfo?: {
    type?: string;
    bankName?: string;
    paymentMethodReferenceId?: string;
    companyConnectionReferenceId?: string;
    userAcceptedPaymentMethod?: boolean;
    isPending?: boolean;
    sourceScope?: string;
    companyId?: string;
    teamId?: string;
  };
}

export interface ManageWellspaceProductSelection {
  companyId: string;
  companyName?: string;
  teamId?: string;
  teamName?: string;
  availableProductIds: string[];
  products?: VendorProduct[];
  alwaysActiveProductIds: string[];
  companyDefaultActiveProductIds: string[];
  teamOverrideActiveProductIds?: string[];
  selectedProductIds: string[];
  activeProductsOverride?: boolean;
  allowanceEligibleProductIds?: string[];
  allowanceControlDisabledProductIds?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TeamCompaniesService {
  private readonly payrollAdminTeamsConfigField = 'payrollAdminTeams';

  constructor(
    private restangular: ApiClient,
  ) { }

  getRawPayrollConfig(company: any): any {
    return this.parsePayrollConfig(company?.payrollConfig);
  }

  getResolvedPayrollConfig(company: any): any {
    const rawPayrollConfig = this.getRawPayrollConfig(company);
    const resolvedPayrollConfig = this.parsePayrollConfig(company?.resolvedPayrollConfig);

    if (Object.keys(resolvedPayrollConfig).length === 0) {
      return rawPayrollConfig;
    }

    return {
      ...rawPayrollConfig,
      ...resolvedPayrollConfig,
    };
  }

  getPayrollAdminTeamsConfig(company: any, resolved = false): any {
    const payrollConfig = resolved
      ? this.getResolvedPayrollConfig(company)
      : this.getRawPayrollConfig(company);

    return this.parsePayrollAdminTeamsConfig(payrollConfig);
  }

  isPayrollAdminCompany(company: any): boolean {
    const payrollConfig = this.getRawPayrollConfig(company);
    return Object.prototype.hasOwnProperty.call(payrollConfig, 'payrollCompany')
      && !!payrollConfig.payrollCompany
      && typeof payrollConfig.payrollCompany === 'object'
      && !Array.isArray(payrollConfig.payrollCompany);
  }

  isCompanyPlusPlan(company: any): boolean {
    const payrollConfig = this.getRawPayrollConfig(company);
    return `${payrollConfig?.plan || ''}`.trim().toLowerCase() === 'plus';
  }

  buildCompanyPlusPlanPayrollConfig(company: any, enabled: boolean): any {
    const nextPayrollConfig = {
      ...this.getRawPayrollConfig(company),
    };

    if (enabled) {
      nextPayrollConfig.plan = 'plus';
    } else {
      delete nextPayrollConfig.plan;
    }

    return nextPayrollConfig;
  }

  buildPayrollAdminTeamsPayrollConfig(
    company: any,
    updates: { defaultPlan?: string; plusPlanIncluded?: boolean },
  ): any {
    const nextPayrollConfig = {
      ...this.getRawPayrollConfig(company),
    };
    const currentPayrollAdminTeamsConfig = this.getPayrollAdminTeamsConfig(company);
    const nextPayrollAdminTeamsConfig = {
      ...currentPayrollAdminTeamsConfig,
      ...updates,
    };

    delete nextPayrollConfig.plan;
    delete nextPayrollConfig.plusPlanIncluded;

    Object.keys(nextPayrollAdminTeamsConfig).forEach((field) => {
      if ((nextPayrollAdminTeamsConfig as any)[field] === undefined) {
        delete (nextPayrollAdminTeamsConfig as any)[field];
      }
    });

    if (Object.keys(nextPayrollAdminTeamsConfig).length > 0) {
      nextPayrollConfig[this.payrollAdminTeamsConfigField] = nextPayrollAdminTeamsConfig;
    } else {
      delete nextPayrollConfig[this.payrollAdminTeamsConfigField];
    }

    return nextPayrollConfig;
  }

  buildProductPayrollDeductionPayrollConfig(
    company: any,
    productPayrollDeductionMap: Record<string, any>,
  ): any {
    const nextPayrollConfig = {
      ...this.getRawPayrollConfig(company),
    };
    const normalizedMap: Record<string, any> = {};
    const payrollDeductionEligibleProductIds = new Set(this.getPayrollDeductionEligibleProductIds(company));

    Object.entries((nextPayrollConfig as any).productPayrollDeductionMap || {}).forEach(([productId, config]: [string, any]) => {
      const normalizedProductId = `${productId || ''}`.trim();
      if (!payrollDeductionEligibleProductIds.has(normalizedProductId)) {
        return;
      }
      const deductionRules = (Array.isArray(config?.deductionRules) ? config.deductionRules : [])
        .map((rule: any) => {
          const deductionCode = `${rule?.deductionCode || ''}`.trim();
          const planId = `${rule?.planId || ''}`.trim();
          const planLabel = `${rule?.planLabel || ''}`.trim();
          const tier = `${rule?.tier || ''}`.trim();
          if (!deductionCode || (!planId && !planLabel && !tier)) {
            return null;
          }
          return {
            ...(planId ? { planId } : {}),
            ...(planLabel ? { planLabel } : {}),
            ...(tier ? { tier } : {}),
            deductionCode,
            ...(rule?.deductionCodeDescription ? { deductionCodeDescription: `${rule.deductionCodeDescription}`.trim() } : {}),
            ...(rule?.deductionCodeId !== undefined && rule?.deductionCodeId !== null
              ? { deductionCodeId: rule.deductionCodeId }
              : {}),
          };
        })
        .filter((rule: any) => !!rule);
      if (normalizedProductId && deductionRules.length > 0) {
        normalizedMap[normalizedProductId] = { deductionRules };
      }
    });

    Object.entries(productPayrollDeductionMap || {}).forEach(([productId, config]) => {
      const normalizedProductId = `${productId || ''}`.trim();
      const deductionCode = `${config?.deductionCode || ''}`.trim();
      const deductionCodeDescription = `${config?.deductionCodeDescription || ''}`.trim();
      if (!payrollDeductionEligibleProductIds.has(normalizedProductId)) {
        return;
      }
      if (!normalizedProductId || !deductionCode) {
        return;
      }

      normalizedMap[normalizedProductId] = {
        ...(normalizedMap[normalizedProductId] || {}),
        deductionCode,
        ...(deductionCodeDescription ? { deductionCodeDescription } : {}),
        ...(config?.deductionCodeId !== undefined && config?.deductionCodeId !== null
          ? { deductionCodeId: config.deductionCodeId }
          : {}),
      };
    });

    if (Object.keys(normalizedMap).length > 0) {
      nextPayrollConfig.productPayrollDeductionMap = normalizedMap;
    } else {
      delete nextPayrollConfig.productPayrollDeductionMap;
    }

    return nextPayrollConfig;
  }

  getAvailableProductIds(company: any): string[] {
    const payrollConfig = this.getResolvedPayrollConfig(company);
    return Array.isArray(payrollConfig?.products) ? payrollConfig.products : [];
  }

  getPayrollAdminBankConnection(company: any): PayrollAdminBankConnection {
    const payrollConfig = this.getRawPayrollConfig(company);
    const connection = this.parsePayrollConfig(payrollConfig?.payrollAdminBankConnection);
    return connection && typeof connection === 'object' ? connection : {};
  }

  getAlwaysActiveProductIds(company: any): string[] {
    const payrollConfig = this.getResolvedPayrollConfig(company);
    return Array.isArray(payrollConfig?.alwaysActiveProducts) ? payrollConfig.alwaysActiveProducts : [];
  }

  getPayrollDeductionDisabledProductIds(company: any): string[] {
    const payrollConfig = this.getResolvedPayrollConfig(company);
    return Array.isArray(payrollConfig?.payrollDeductionDisabledProducts)
      ? payrollConfig.payrollDeductionDisabledProducts
      : [];
  }

  getAllowanceControlDisabledProductIds(company: any): string[] {
    const payrollConfig = this.getResolvedPayrollConfig(company);
    return Array.isArray(payrollConfig?.allowanceControlDisabledProducts)
      ? payrollConfig.allowanceControlDisabledProducts
      : [];
  }

  getPayrollDeductionEligibleProductIds(company: any): string[] {
    const disabledProductIds = new Set(this.getPayrollDeductionDisabledProductIds(company));
    return this.getAvailableProductIds(company).filter((productId) => !disabledProductIds.has(productId));
  }

  getCompany(id: string): Promise<any> {
    return this.restangular.one('teams').one('companies', id).get().toPromise();
  }

  getPayrollCompanyWorkspaceContext(companyId: string): Promise<PayrollCompanyWorkspaceContext> {
    return this.restangular
      .one('payroll')
      .one('company', companyId)
      .one('workspace-context')
      .get()
      .toPromise()
      .then((response: any) => response?.plain?.() || response);
  }

  getCompanyByName(name: string): Promise<any> {
    return this.restangular.one('teams').one('companies').one('name', name).get().toPromise();
  }

  getCompanyWithLinkedTeam(teamId: string): Promise<any> {
    return this.restangular.one('teams').one('companies').one('linked', teamId).get().toPromise();
  }

  getCompanyByNameAndType(name: string, type: string): Promise<any> {
    return this.restangular
      .one('teams')
      .one('companies')
      .one('name', name)
      .one('type', type).get().toPromise();
  }

  getCompanies(role = 'supportmanager'): Promise<any> {
    return this.restangular.one('teams').one('companies').one('role', role).getList().toPromise();
  }

  deleteCompany(companyId: string): Promise<any> {
    return this.restangular.one('teams').one('companies', companyId).remove().toPromise();
  }

  modifyCompany(companyId: string, fields: any): Promise<any> {
    return this.restangular.one('teams').one('companies', companyId).customPUT({
      ...fields
    }).toPromise();
  }

  getCompanyProductSelection(companyId: string): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('products')
      .get()
      .toPromise();
  }

  saveCompanyProductSelection(
    companyId: string,
    productIds: string[],
  ): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('products')
      .customPUT({ productIds })
      .toPromise();
  }

  async modifyCompanyPayrollConfig(
    companyId: string,
    buildNextPayrollConfig: (latestPayrollConfig: any, latestCompany: any) => any,
  ): Promise<any> {
    const latestCompany = await this.getCompany(companyId);
    const latestPayrollConfig = this.getRawPayrollConfig(latestCompany);
    const nextPayrollConfig = this.parsePayrollConfig(
      buildNextPayrollConfig(latestPayrollConfig, latestCompany)
    );

    await this.modifyCompany(companyId, { payrollConfig: nextPayrollConfig });

    return {
      ...(latestCompany || {}),
      payrollConfig: nextPayrollConfig,
    };
  }

  getPayrollAdminBankSetupIntent(companyId: string): Promise<any> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('bank-setup-intent')
      .get()
      .toPromise();
  }

  finalizePayrollAdminBankConnection(
    companyId: string,
    setupIntentId: string,
  ): Promise<PayrollAdminBankConnection> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('bank-connection')
      .customPUT({ setupIntentId })
      .toPromise();
  }

  deletePayrollAdminBankConnection(companyId: string): Promise<any> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('bank-connection')
      .remove()
      .toPromise();
  }

  getGroupProductOnboardingStatuses(companyId: string): Promise<CompanyGroupProductOnboardingRow[]> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('group-product-onboarding')
      .getList()
      .toPromise();
  }

  submitGroupProductOnboardingRequest(
    companyId: string,
    requestedVendorProductId: string,
    requestNote = '',
  ): Promise<CompanyGroupProductOnboardingRow> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('group-product-onboarding')
      .one('request')
      .customPOST({
        requestedVendorProductId,
        requestNote,
      })
      .toPromise();
  }

  sendBrokerOnboardingNotification(
    companyId: string,
    beforeBrokers: PayrollCompanyBrokerNotificationBroker[],
    afterBrokers: PayrollCompanyBrokerNotificationBroker[],
  ): Promise<PayrollCompanyBrokerNotificationResult> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('broker-onboarding-notification')
      .customPOST({
        afterBrokers,
        beforeBrokers,
      })
      .toPromise();
  }

  markGroupProductOnboardingReady(
    companyId: string,
    requestedVendorProductId: string,
  ): Promise<CompanyGroupProductOnboardingRow> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('group-product-onboarding')
      .one(requestedVendorProductId)
      .one('ready')
      .customPUT({})
      .toPromise();
  }

  deleteGroupProductOnboardingRequest(
    companyId: string,
    requestedVendorProductId: string,
  ): Promise<CompanyGroupProductOnboardingRow> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('group-product-onboarding')
      .one(requestedVendorProductId)
      .remove()
      .toPromise();
  }

  getGroupProductOnboardingReport(): Promise<CompanyGroupProductOnboardingRow[]> {
    return this.restangular
      .one('teams')
      .one('companies')
      .one('group-product-onboarding')
      .one('report')
      .getList()
      .toPromise();
  }

  createCompany(
    name: string, payrollConfig: any, domainName: string, type: string, parentId: string,
    EIN: string, SIC: string, address1: string, address2: string, city: string, state: string,
    postcode: string, metadata = {},
  ): Promise<any> {
    return this.restangular.one('teams').one('companies').customPOST({
      name,
      payrollConfig,
      domainName,
      type,
      parentId,
      EIN, SIC, address1, address2, city, state, postcode, metadata
    }).toPromise();
  }

  uploadLogo(companyId: string, data: any): Promise<any> {
    return this.restangular.one('teams').one('companies', companyId).one('logo').customPUT(data, undefined, undefined, { 'content-type': 'image/*' }).toPromise();
  }

  private parsePayrollAdminTeamsConfig(payrollConfig: any): any {
    const normalizedPayrollConfig = this.parsePayrollConfig(payrollConfig);
    const nestedPayrollAdminTeamsConfig = this.parsePayrollConfig(
      normalizedPayrollConfig?.[this.payrollAdminTeamsConfigField]
    );
    const payrollAdminTeamsConfig: any = {};

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'defaultPlan')) {
      payrollAdminTeamsConfig.defaultPlan = nestedPayrollAdminTeamsConfig.defaultPlan;
    }

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = nestedPayrollAdminTeamsConfig.plusPlanIncluded;
    } else if (Object.prototype.hasOwnProperty.call(normalizedPayrollConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = normalizedPayrollConfig.plusPlanIncluded;
    }

    return payrollAdminTeamsConfig;
  }

  private parsePayrollConfig(value: any): any {
    if (!value) {
      return {};
    }

    if (typeof value === 'object') {
      return value;
    }

    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }

    return {};
  }
}
